import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Api } from 'vopay';
import { ApiProvider } from 'shared';
import { api } from '@/services/api.config';

type AccountScopedApiProviderProps = {
  /** The account's own JWT, as the listing returns it with `ShowToken`. */
  bearer: string;
  /**
   * A view-only partner role. The shared features offer their row actions to every viewer, so the ones
   * that mutate through this api — clearing a transaction's flag, blocking, editing, deactivating a
   * sub-account or saving its permissions — are refused here with a message the feature shows in its
   * own error dialog or toast; everything that only reads still works.
   */
  readOnly?: boolean;
  children: ReactNode;
};

/**
 * Re-binds, to one account's bearer, the api methods the shared features mounted on the account pages
 * call — the account routes they use (`/account/transactions`, `/account/balances`,
 * `/account/client-accounts` and its labels and balance range, `/account/subaccount` and its block,
 * edit, deactivate and permissions routes) authenticate as the account, not the partner session. Each
 * method keeps the api's own optional `bearer` parameter, so a call that passes its own still wins, and
 * every method not listed resolves the partner session as before. Shared never learns which host it is
 * in.
 */
export default function AccountScopedApiProvider({
  bearer,
  readOnly = false,
  children,
}: AccountScopedApiProviderProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.AccountScopedApiProvider' });

  const scopedApi = useMemo<Api>(() => {
    // What a mutating method becomes for a view-only role: the shared feature's own catch surfaces it.
    const refuseWrite = async () => {
      throw new Error(t('viewOnly', { defaultValue: 'A view-only user cannot make this change.' }));
    };

    return {
      ...api,
      fetchTransactionsHistory: (params, source, requestBearer) =>
        api.fetchTransactionsHistory(params, source, requestBearer ?? bearer),
      fetchAccountBalances: (params, source, requestBearer) =>
        api.fetchAccountBalances(params, source, requestBearer ?? bearer),
      postConfirmTransaction: readOnly
        ? refuseWrite
        : (params, source, requestBearer) => api.postConfirmTransaction(params, source, requestBearer ?? bearer),
      fetchClientAccountList: (params, source, requestBearer) =>
        api.fetchClientAccountList(params, source, requestBearer ?? bearer),
      fetchClientAccountLabels: (params, source, requestBearer) =>
        api.fetchClientAccountLabels(params, source, requestBearer ?? bearer),
      fetchClientAccountBalanceRange: (source, requestBearer) =>
        api.fetchClientAccountBalanceRange(source, requestBearer ?? bearer),
      fetchSubAccounts: (params, source, requestBearer) =>
        api.fetchSubAccounts(params, source, requestBearer ?? bearer),
      fetchSubAccountPermissions: (params, source, requestBearer) =>
        api.fetchSubAccountPermissions(params, source, requestBearer ?? bearer),
      saveSubAccountPermissions: readOnly
        ? refuseWrite
        : (params, source, requestBearer) => api.saveSubAccountPermissions(params, source, requestBearer ?? bearer),
      blockAccounts: readOnly
        ? refuseWrite
        : (params, source, requestBearer) => api.blockAccounts(params, source, requestBearer ?? bearer),
      unblockAccounts: readOnly
        ? refuseWrite
        : (params, source, requestBearer) => api.unblockAccounts(params, source, requestBearer ?? bearer),
      deactivateSubaccount: readOnly
        ? refuseWrite
        : (params, source, requestBearer) => api.deactivateSubaccount(params, source, requestBearer ?? bearer),
      editSubaccount: readOnly
        ? refuseWrite
        : (params, source, requestBearer) => api.editSubaccount(params, source, requestBearer ?? bearer),
      // The Users feature signs every call with the account's bearer itself, so these need no rebinding —
      // only the view-only refusal on its writes (ID-13429).
      postCreateAccountUser: readOnly ? refuseWrite : api.postCreateAccountUser,
      postDeleteAccountUser: readOnly ? refuseWrite : api.postDeleteAccountUser,
      postClientResetPasswordEmail: readOnly ? refuseWrite : api.postClientResetPasswordEmail,
      postAccountUserPermissions: readOnly ? refuseWrite : api.postAccountUserPermissions,
      postCreateAccountUserRole: readOnly ? refuseWrite : api.postCreateAccountUserRole,
      postUpdateAccountUserRole: readOnly ? refuseWrite : api.postUpdateAccountUserRole,
      deleteAccountUserRole: readOnly ? refuseWrite : api.deleteAccountUserRole,
      assignUserRole: readOnly ? refuseWrite : api.assignUserRole,
      unassignUserRole: readOnly ? refuseWrite : api.unassignUserRole,
    };
  }, [bearer, readOnly, t]);

  return <ApiProvider api={scopedApi}>{children}</ApiProvider>;
}
